/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * LoginPage.tsx — Logi\1 \2age (services/operator-web-app).
 */

/**
 * Plan 2.1: Minimal operator login — set operator ID before connecting.
 * Value is stored in sessionStorage and used by connect() and voice transport.
 */
import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@/components/ui';
import { getOperatorId, setOperatorId } from '@/config/env';
import { appConfig } from '@/config/env';
import { LocaleSwitcher, useTranslation } from '@/i18n';

export function LoginPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const [operatorId, setOperatorIdState] = useState(() => getOperatorId());
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = operatorId.trim();
    if (!trimmed) {
      setError(t('login.operator_required'));
      return;
    }
    setError(null);
    setOperatorId(trimmed);
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <LocaleSwitcher className="mb-4" />
      <main
        className="w-full max-w-sm rounded-xl border p-6 shadow-lg glass-panel"
        id="main-content"
        tabIndex={-1}
      >
        <h1 className="text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
          {t('login.title')}
        </h1>
        <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
          {t('login.description')}{' '}
          <code className="text-xs px-1 rounded" style={{ background: 'var(--toggle-bg)' }}>
            {appConfig.operatorId}
          </code>
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <label
            htmlFor="operator-id"
            className="block text-sm font-medium"
            style={{ color: 'var(--text-secondary)' }}
          >
            {t('login.operator_id')}
          </label>
          <Input
            id="operator-id"
            type="text"
            value={operatorId}
            onChange={(e) => setOperatorIdState(e.target.value)}
            placeholder={appConfig.operatorId}
            autoComplete="username"
            aria-describedby={error ? 'operator-id-error' : undefined}
            aria-invalid={!!error}
            className="w-full"
          />
          {error && (
            <p
              id="operator-id-error"
              className="text-sm text-amber-600 dark:text-amber-400"
              role="alert"
            >
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" className="w-full">
            {t('login.submit')}
          </Button>
        </form>
        <p className="mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
          {t('login.after_sign_in')}
        </p>
      </main>
    </div>
  );
}
